import { useState, useMemo, useCallback } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { 
  BarChart4, 
  AlertTriangle,
  BookOpen,
  Target,
  Bell,
  Layers,
  LayoutGrid,
  Table2
} from 'lucide-react'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import PerformanceOnePager from './PerformanceOnePager'
import ObjectiveSettingDialog from './ObjectiveSettingDialog'
import ObjectiveMatrixView from './ObjectiveMatrixView'
import { ViewModeToggle } from '@/components/journey/ViewModeToggle'

// Import modular components
import MemberKPAPanel from './manager/MemberKPAPanel'
import StrategicCascadeViewer from './manager/StrategicCascadeViewer'

// Import types
import { EnhancedEvidenceEntry, JITCheckIn, KPAData } from '@/types/evidence'
import { CollaborationSession } from '@/types/objectives'

// Import enterprise progress calculation
import { calculateProgress } from '@/utils/kpiProgress'
import type { MeasurementDirection, MeasurementMode } from '@/types/kpi'

interface EmployeeKPAViewProps {
  kpas: KPAData[]
  employeeId: string // Actual employee ID for permission checks
  employeeName: string
  employeeRole: string
  reviewPeriod?: 'MIDYEAR' | 'YEAREND' // Filter measurement history by review period
  /**
   * Simple ↔ Detailed presentation. Simple (default) hides RAG colors,
   * At-Risk pills, and progress percentages on the Overview/KPA cards to
   * make the page less metric-anxiety-driven. Detailed restores them.
   * Comes from the shared `useJourneyViewMode` hook in the parent.
   */
  journeyViewMode?: 'simple' | 'detailed'
  
  // Evidence handlers
  evidence: EnhancedEvidenceEntry[]
  onAddEvidence: (evidence: Omit<EnhancedEvidenceEntry, 'id' | 'createdBy' | 'updatedAt'>) => Promise<EnhancedEvidenceEntry | undefined> | void
  onUpdateEvidence?: (evidenceId: string, updates: Partial<EnhancedEvidenceEntry>) => void
  onUpdateEvidenceClassification?: (evidenceId: string, classification: EnhancedEvidenceEntry['classification']) => void
  onRefreshEvidence?: () => Promise<void>
  onDeleteEvidence: (evidenceId: string) => void
  onUploadDocument: (file: File, evidenceData: Partial<EnhancedEvidenceEntry>) => Promise<void>
  
  // Individual Objectives handlers
  onCreateObjective: (objective: any) => void
  onUpdateObjective: (objectiveId: number, updates: any) => void
  onDeleteObjective: (objectiveId: number) => void
  onViewObjectiveDetails: (objective: any) => void
  onConfigureObjectives?: (initialTab?: 'pending' | 'active' | 'rejected' | 'allocate') => void
  
  // KPI handlers
  onViewKPIDetails: (kpi: any) => void
  
}

export default function EmployeeKPAView({
  kpas,
  employeeId,
  employeeName,
  employeeRole,
  reviewPeriod,
  journeyViewMode = 'simple',
  evidence,
  onAddEvidence,
  onUpdateEvidence,
  onUpdateEvidenceClassification,
  onRefreshEvidence,
  onDeleteEvidence,
  onUploadDocument,
  onCreateObjective,
  onUpdateObjective,
  onDeleteObjective,
  onViewObjectiveDetails,
  onConfigureObjectives,
  onViewKPIDetails
}: EmployeeKPAViewProps) {
  const [activeTab, setActiveTab] = useState<string>('overview')
  const [highlightedKPIId] = useState<string | null>(null)
  const [isObjectiveDialogOpen, setIsObjectiveDialogOpen] = useState(false)
  
  // View mode: 'cards' (default tabbed view) or 'matrix' (table view)
  const VIEW_MODE_KEY = `employeeKPA_viewMode_${employeeId}`
  const [viewMode, setViewMode] = useState<'cards' | 'matrix'>(() => {
    const stored = sessionStorage.getItem(VIEW_MODE_KEY)
    return (stored === 'matrix') ? 'matrix' : 'cards'
  })
  
  const handleSetViewMode = useCallback((mode: 'cards' | 'matrix') => {
    setViewMode(mode)
    sessionStorage.setItem(VIEW_MODE_KEY, mode)
  }, [VIEW_MODE_KEY])
  
  // Switch from matrix to card view, optionally navigating to a specific KPA tab
  const handleSwitchToCardView = useCallback((kpaId?: string) => {
    handleSetViewMode('cards')
    if (kpaId) setActiveTab(`kpa-${kpaId}`)
  }, [handleSetViewMode])

  // Display-mode controls (Show details switch + Card|Matrix segmented control).
  // Shared by the desktop header row and the mobile header row so the markup
  // lives in exactly one place. A right-anchored SIBLING of the tab strip — it
  // never participates in the tab wrapping, so it stays put regardless of how
  // many rows the KPA tabs wrap into. Tobacco-on-bone in Simple, heritage-blue
  // in Detailed (matches the switch).
  const renderDisplayControls = () => {
    const segBase = 'inline-flex items-center gap-1.5 h-8 px-2.5 sm:px-3 rounded-md text-xs font-semibold whitespace-nowrap transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1'
    const segActive = journeyViewMode === 'simple' ? 'bg-quarterly-tobacco text-white shadow-sm' : 'bg-heritage-blue text-white shadow-sm'
    const segIdle = journeyViewMode === 'simple' ? 'bg-transparent text-quarterly-ink-muted hover:text-quarterly-tobacco' : 'bg-transparent text-heritage-blue/70 hover:text-heritage-blue hover:bg-heritage-blue-50'
    const segRing = journeyViewMode === 'simple' ? 'focus-visible:ring-quarterly-tobacco' : 'focus-visible:ring-heritage-blue'
    return (
      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
        {/* Card | Matrix segmented control — always visible (the only way back
            from matrix), sits on top of the cluster. */}
        <div
          role="tablist"
          aria-label="Display mode"
          className={cn(
            'inline-flex flex-shrink-0 items-center gap-0.5 rounded-lg p-0.5',
            journeyViewMode === 'simple' ? 'bg-quarterly-bone ring-1 ring-quarterly-rule' : 'bg-heritage-blue-50 ring-1 ring-heritage-blue-100',
          )}
        >
          <button type="button" role="tab" aria-selected={viewMode === 'cards'} onClick={() => handleSetViewMode('cards')} title="Card view"
            className={cn(segBase, segRing, viewMode === 'cards' ? segActive : segIdle)}>
            <LayoutGrid className="h-4 w-4" />
            <span className="hidden sm:inline">Card</span>
          </button>
          <button type="button" role="tab" aria-selected={viewMode === 'matrix'} onClick={() => handleSetViewMode('matrix')} title="Matrix view"
            className={cn(segBase, segRing, viewMode === 'matrix' ? segActive : segIdle)}>
            <Table2 className="h-4 w-4" />
            <span className="hidden sm:inline">Matrix</span>
          </button>
        </div>
        {/* Show details — Card view ONLY (irrelevant to the matrix table),
            sits beneath the view switch. */}
        {viewMode === 'cards' && <ViewModeToggle />}
      </div>
    )
  }
  
  // Calculate dynamic KPA progress from objectives (same logic as MemberKPAPanel)
  const calculateKPAProgress = (kpa: KPAData) => {
    // Filter out rejected objectives
    const activeObjectives = kpa.individualObjectives.filter(obj => obj.status !== 'rejected')
    
    if (!activeObjectives || activeObjectives.length === 0) {
      return kpa.averageProgress || 0
    }
    
    // Calculate each objective's progress (based on its KPIs)
    const totalProgress = activeObjectives.reduce((sum, objective) => {
      if (!objective.kpis || objective.kpis.length === 0) {
        return sum + (objective.progress || 0)
      }
      
      // Calculate objective progress from KPIs using enterprise calculation
      const objectiveProgress = objective.kpis.reduce((kpiSum, kpi) => {
        // Use current_value directly for employee view (no period filtering in this component)
        const mode = ((kpi as any).measurement_mode || 'numeric') as MeasurementMode
        const direction = ((kpi as any).measurement_direction || 'higher_is_better') as MeasurementDirection
        const progressResult = calculateProgress(
          kpi.current_value ?? undefined,
          kpi.target_value,
          direction,
          mode
        )
        return kpiSum + progressResult.progress
      }, 0) / objective.kpis.length
      
      return sum + objectiveProgress
    }, 0)
    
    return Math.round(totalProgress / activeObjectives.length)
  }
  
  // Display KPAs (sorted by sort_order for consistent ordering)
  // Order: 1. Operating Financial, 2. Internal Business, 3. Culture, 4. Customer and Stakeholder
  const displayKPAs = useMemo(() => {
    return [...kpas].sort((a, b) => ((a as any).sort_order || 999) - ((b as any).sort_order || 999))
  }, [kpas])
  
  // Calculate aggregate statistics with dynamic progress
  const aggregateStats = useMemo(() => {
    // Count only active (non-rejected) objectives
    const totalObjectives = displayKPAs.reduce((sum, kpa) => 
      sum + kpa.individualObjectives.filter(obj => obj.status !== 'rejected').length, 0)
    const totalKPIs = displayKPAs.reduce((sum, kpa) => sum + kpa.underlyingKPIs.length, 0)
    const totalEvidence = displayKPAs.reduce((sum, kpa) => sum + kpa.totalEvidence, 0)
    // Calculate average progress dynamically from KPAs
    const avgProgress = displayKPAs.reduce((sum, kpa) => sum + calculateKPAProgress(kpa), 0) / displayKPAs.length || 0
    
    return {
      totalObjectives,
      totalKPIs,
      totalEvidence,
      avgProgress: Math.round(avgProgress)
    }
  }, [displayKPAs])
  
  // Employee performance summary
  const employeeDetails = {
    // RAG risk-count alerts intentionally suppressed in the UI (fresh platform:
    // no real progress data yet, so every KPA would falsely surface as at-risk).
    alerts: [] as string[],
    kpas: displayKPAs.map(kpa => ({
      id: kpa.id,
      name: kpa.name,
      display_name: kpa.display_name,
      status: kpa.riskLevel === 'low' ? 'on-track' as const : kpa.riskLevel === 'medium' ? 'at-risk' as const : 'off-track' as const,
      progress: calculateKPAProgress(kpa), // Dynamic progress
      objectivesCount: kpa.individualObjectives.filter(obj => obj.status !== 'rejected').length,
      kpisCount: kpa.underlyingKPIs.length,
      evidenceCount: kpa.totalEvidence
    })),
    // Additional fields for PerformanceOnePager
    lastReviewDate: undefined,
    nextReviewDate: undefined,
    developmentGoals: [] as string[],
    completedGoals: 0,
    role: employeeRole,
    // Get functional area from first KPI if available, otherwise use a generic default
    functionalArea: displayKPAs[0]?.underlyingKPIs?.[0]?.functional_area || 'Not specified'
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col min-w-0 max-w-full"
    >
      {/* Main Content. Surface flips to the warm bone palette in Simple
          mode so the Quarterly editorial treatment isn't pasted onto a
          stark white frame. Tab chrome is restyled below to match. */}
      <div className={`-mx-3 sm:-mx-4 md:-mx-6 min-w-0 max-w-full ${journeyViewMode === 'simple' ? 'bg-quarterly-bone' : 'bg-white'}`}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col">
          {/* STABLE CONTROL BAR (D1/D4) — always rendered in BOTH card and
              matrix modes, OUTSIDE the <TabsList> so it never participates in
              the wrapping tab row. Holds: a quiet surface label, the existing
              "Show details" switch, a thin rule, and a real 2-segment
              Card | Matrix segmented control. This is now the top-most sticky
              chrome; the KPA tab strip / mobile Select drop one rung below and
              render only in card mode. Styled tobacco-on-bone in Simple and
              heritage-blue in Detailed to match the switch (D6). */}
          {/* COMBINED STICKY HEADER (single band, top-[69px]). The KPA nav and
              the display controls share ONE row so there is no wasted standalone
              control band. The control cluster is a right-anchored SIBLING of the
              wrapping TabsList (not an item inside it), so it stays put on the
              first row regardless of how many rows the KPA tabs wrap into. In
              matrix mode the tab strip is replaced by a short label; the controls
              persist so the user can switch back. Only ONE sticky layer now sits
              below the journey bar (which is top-0, ~69px tall). */}
          <div
            className={cn(
              'sticky top-[69px] z-30 border-b shadow-sm',
              journeyViewMode === 'simple'
                ? 'bg-quarterly-bone border-quarterly-rule'
                : 'bg-white border-gray-200',
            )}
          >
            {/* MOBILE: compact right-aligned controls row, then the KPA Select
                (card mode only). Width-constrained, so a control row is not waste. */}
            <div className="md:hidden p-3 space-y-2">
              <div className="flex items-center justify-end">
                {renderDisplayControls()}
              </div>
              {viewMode === 'cards' && (
                <Select value={activeTab} onValueChange={(val) => setActiveTab(val)}>
                  <SelectTrigger className="w-full h-11 bg-white border-gray-200 text-sm font-semibold">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="overview">Overview</SelectItem>
                    {displayKPAs.map((kpa) => (
                      <SelectItem key={kpa.id} value={`kpa-${kpa.id}`}>
                        {kpa.display_name || kpa.name}
                        {journeyViewMode === 'detailed' && ` — ${calculateKPAProgress(kpa)}%`}
                      </SelectItem>
                    ))}
                    <SelectItem value="strategic">Strategic Context</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* DESKTOP: KPA tab strip (card mode) OR a short matrix label on the
                left, sharing the row with the display controls on the right.
                items-start + a min-h-14 controls wrapper keeps the controls
                aligned with the FIRST tab row when the strip wraps. */}
            <div className="hidden md:flex items-start gap-3 w-full min-w-0 max-w-full p-1.5">
              {viewMode === 'matrix' ? (
                <div className={cn('flex-1 inline-flex items-center gap-2 min-h-14 px-3 text-sm font-medium', journeyViewMode === 'simple' ? 'text-quarterly-ink-muted' : 'text-gray-600')}>
                  <Table2 className="h-4 w-4 flex-shrink-0" />
                  <span>Matrix — all KPAs at a glance</span>
                </div>
              ) : (
                <TabsList className="flex-1 flex flex-wrap justify-start items-stretch gap-1 min-w-0 max-w-full rounded-none border-0 shadow-none h-auto p-0 bg-transparent">
                  {/* Overview Tab */}
                  <TabsTrigger
                    value="overview"
                    className="gap-1.5 h-auto min-h-14 px-3 py-2 flex-1 min-w-[160px] justify-center rounded-lg text-gray-600 data-[state=active]:bg-heritage-blue data-[state=active]:text-white"
                  >
                    <div className="flex items-center gap-1.5">
                      <BarChart4 className="h-4 w-4 flex-shrink-0" />
                      <span className="text-sm font-medium whitespace-nowrap">Overview</span>
                    </div>
                  </TabsTrigger>

                  {/* Dynamic KPA Tabs */}
                  {displayKPAs.map((kpa) => {
                    return (
                      <TabsTrigger
                        key={kpa.id}
                        value={`kpa-${kpa.id}`}
                        className={cn(
                          'gap-2 h-auto min-h-14 px-3 py-2 flex-1 min-w-[160px] justify-center rounded-lg border data-[state=active]:bg-heritage-blue data-[state=active]:text-white data-[state=active]:border-heritage-blue',
                          // Fill + border so the KPA tabs read as distinct chips
                          // (same treatment as the manager-side MemberDetailsDialog /
                          // MemberKPAPanel). Blue on the white Detailed surface;
                          // a quiet white-on-bone chip in Simple so it stays calm.
                          journeyViewMode === 'simple'
                            ? 'bg-white/70 border-quarterly-rule text-quarterly-ink-muted'
                            : 'bg-blue-50/60 border-blue-100 text-gray-600',
                        )}
                        title={kpa.display_name || kpa.name}
                      >
                        <div className="flex items-center gap-1.5">
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <BookOpen className="h-4 w-4" />
                          </div>
                          <span className="text-sm font-medium text-center whitespace-normal break-words">
                            {kpa.display_name || kpa.name}
                          </span>
                          {journeyViewMode === 'detailed' && (
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <span className="text-xs opacity-75">{calculateKPAProgress(kpa)}%</span>
                            </div>
                          )}
                        </div>
                      </TabsTrigger>
                    )
                  })}

                  {/* Strategic Context Tab */}
                  <TabsTrigger
                    value="strategic"
                    className="gap-1.5 h-auto min-h-14 px-3 py-2 flex-1 min-w-[160px] justify-center rounded-lg text-gray-600 data-[state=active]:bg-heritage-blue data-[state=active]:text-white"
                  >
                    <div className="flex items-center gap-1.5">
                      <Layers className="h-4 w-4 flex-shrink-0" />
                      <span className="text-sm font-medium whitespace-nowrap">Strategic Context</span>
                    </div>
                  </TabsTrigger>
                </TabsList>
              )}

              {/* Display controls — right-anchored sibling. pt-1.5 nudges the
                  Card/Matrix switch to roughly centre on the first tab row; the
                  Show details toggle hangs just beneath it (card mode only). */}
              <div className="flex-shrink-0 pt-1.5">
                {renderDisplayControls()}
              </div>
            </div>
          </div>

          {/* Matrix View — replaces all tab content when active */}
          {viewMode === 'matrix' && (
            <div className="px-3 py-4 sm:px-6 sm:py-5 md:px-8 md:py-6">
              <ObjectiveMatrixView
                kpas={kpas}
                employeeId={employeeId}
                employeeName={employeeName}
                employeeRole={employeeRole}
                reviewPeriod={reviewPeriod}
                evidence={evidence}
                onAddEvidence={onAddEvidence}
                onUpdateEvidence={onUpdateEvidence}
                onUpdateEvidenceClassification={onUpdateEvidenceClassification}
                onRefreshEvidence={onRefreshEvidence}
                onDeleteEvidence={onDeleteEvidence}
                onUploadDocument={onUploadDocument}
                onCreateObjective={onCreateObjective}
                onUpdateObjective={onUpdateObjective}
                onDeleteObjective={onDeleteObjective}
                onViewObjectiveDetails={onViewObjectiveDetails}
                onConfigureObjectives={onConfigureObjectives}
                onViewKPIDetails={onViewKPIDetails}
                onSwitchToCardView={handleSwitchToCardView}
              />
            </div>
          )}

          {/* Card View — standard tabbed content */}
          {viewMode === 'cards' && (
            <>
          {/* Overview Tab Content - Enhanced One-Pager */}
          <TabsContent value="overview" className="px-3 py-4 sm:px-6 sm:py-5 md:px-8 md:py-6 m-0">
            <PerformanceOnePager
              employeeName={employeeName}
              kpas={kpas}
              lastReviewDate={employeeDetails.lastReviewDate}
              nextReviewDate={employeeDetails.nextReviewDate}
              reviewPeriod={reviewPeriod}
              developmentPlan={{
                goals: employeeDetails.developmentGoals || [],
                completedGoals: employeeDetails.completedGoals || 0,
                totalGoals: employeeDetails.developmentGoals?.length || 0
              }}
              alerts={employeeDetails.alerts}
              onNavigateToKPA={(kpaId) => setActiveTab(`kpa-${kpaId}`)}
              onNavigateToObjective={(objectiveId) => {
                // Find the KPA that contains this objective and navigate to it
                const kpa = kpas.find(k => k.individualObjectives.some(obj => obj.id === objectiveId))
                if (kpa) {
                  setActiveTab(`kpa-${kpa.id}`)
                }
              }}
              onConfigureObjectives={onConfigureObjectives}
              userRole="employee"
              journeyViewMode={journeyViewMode}
            />
          </TabsContent>

          {/* Dynamic KPA Content Tabs */}
          {displayKPAs.map((kpa) => {
            // Filter out rejected objectives for this KPA
            const filteredKPA = {
              ...kpa,
              individualObjectives: kpa.individualObjectives.filter(obj => obj.status !== 'rejected')
            }
            
            // Get all KPI IDs for this KPA (from underlyingKPIs and from objectives' KPIs)
            const kpaKPIIds = new Set<string>()
            kpa.underlyingKPIs?.forEach(kpi => kpaKPIIds.add(kpi.id))
            kpa.individualObjectives?.forEach(obj => {
              obj.kpis?.forEach(kpi => kpaKPIIds.add(kpi.id))
            })

            // Get all individual-objective IDs for this KPA (evidence carries
            // objectiveId as a stringified individual_objective_id)
            const kpaObjectiveIds = new Set<string>()
            filteredKPA.individualObjectives?.forEach(obj => kpaObjectiveIds.add(String(obj.id)))

            // Filter evidence for THIS KPA only — match on a real link:
            //  • direct KPA link (kpaId), or
            //  • a KPI that belongs to this KPA, or
            //  • an individual objective that belongs to this KPA.
            // Genuinely general/unlinked entries (no kpaId, no matching KPI,
            // no matching objective) are NOT shown here — they live in the
            // top-level "my Impact Portfolio" tab under the General group.
            // (Previously a `e.kpaId === null` catch-all leaked every general
            // entry onto every KPA tab — the "same entries on each tab" bug.)
            const kpaEvidence = evidence.filter(e =>
              e.kpaId === kpa.id ||
              (e.kpiId && kpaKPIIds.has(e.kpiId)) ||
              (e.objectiveId && kpaObjectiveIds.has(e.objectiveId))
            )
            
            if (import.meta.env.DEV) console.log(`📊 KPA "${kpa.id}" Evidence Filter:`, {
              kpaName: kpa.name,
              totalEvidence: evidence.length,
              kpaKPIIds: Array.from(kpaKPIIds),
              matchedEvidence: kpaEvidence.length,
              matchedItems: kpaEvidence.map(e => ({ 
                id: e.id, 
                title: e.title, 
                period: e.reviewPeriod,
                kpaId: e.kpaId,
                kpiId: e.kpiId,
                matchReason: e.kpaId === kpa.id ? 'kpaId' : (e.kpiId && kpaKPIIds.has(e.kpiId)) ? 'kpiId' : 'objectiveId'
              }))
            })
            
            return (
            <TabsContent key={kpa.id} value={`kpa-${kpa.id}`} className="px-3 sm:px-6 md:px-8 m-0">
              <MemberKPAPanel
                kpa={filteredKPA}
                memberId={employeeId} // Actual employee ID for permission checks
                memberName={employeeName}
                currentUserId={employeeId} // For employee view, currentUserId is the same as memberId
                userRole="employee" // Employee adds evidence as 'employee' source
                reviewPeriod={reviewPeriod}
                journeyViewMode={journeyViewMode}

                // Filter data for this specific KPA - match by kpaId, objectiveId, or kpiId
                evidence={kpaEvidence}
                
                // Evidence handlers
                onAddEvidence={onAddEvidence}
                onUpdateEvidence={onUpdateEvidence}
                onUpdateEvidenceClassification={onUpdateEvidenceClassification}
                onRefreshEvidence={onRefreshEvidence}
                onDeleteEvidence={onDeleteEvidence}
                onUploadDocument={onUploadDocument}
                
                // Individual Objectives handlers
                onCreateObjective={onCreateObjective}
                onUpdateObjective={onUpdateObjective}
                onDeleteObjective={onDeleteObjective}
                onViewObjectiveDetails={onViewObjectiveDetails}
                // No approval handlers for employee self-view, but allow navigation to configure view
                onOpenObjectiveConfig={onConfigureObjectives}
                
                // KPI handlers
                onViewKPIDetails={onViewKPIDetails}
                
                // Navigation support
                highlightedKPIId={highlightedKPIId}
              />
            </TabsContent>
          )})}

          {/* Strategic Context Tab */}
          <TabsContent value="strategic" className="px-3 py-4 sm:px-6 sm:py-5 md:px-8 md:py-6 m-0">
            <StrategicCascadeViewer 
              cascadeData={kpas.flatMap(kpa => 
                // Group by individual objective, not by KPI - filter out rejected objectives
                kpa.individualObjectives
                  .filter(obj => obj.status !== 'rejected')
                  .map(obj => ({
                  // L1: Strategic Objective (SCALE, QUALITY, CULTURE, IMPACT)
                  pillar_name: obj.strategic_objective?.name || 'Uncategorized',
                  // L2: Strategic Initiative (if available, otherwise use a meaningful default)
                  strategic_objective_name: obj.strategic_initiative?.name || 
                    (obj.strategic_objective?.display_name ? `${obj.strategic_objective.display_name} (Needs Strategic Initiative)` : 'Not Aligned'),
                  // KPA (functional area)
                  kpa_name: kpa.display_name || kpa.name,
                  // Store full strategic context for detailed view
                  strategic_context: {
                    l1: obj.strategic_objective,
                    l2: obj.strategic_initiative,
                    l3: obj.operational_initiative
                  },
                  individual_objective: {
                    id: obj.id,
                    title: obj.title,
                    description: obj.description ?? null,
                    role_type: obj.role_type,
                    owner: obj.owner,
                    functional_area: obj.functional_area,
                    status: obj.status,
                    progress: obj.progress,
                    start_date: obj.start_date ?? null,
                    end_date: obj.end_date ?? null
                  },
                  // Include ALL KPIs for this objective in one entry
                  kpis: (obj.kpis || []).map(kpi => ({
                    id: kpi.id,
                    name: kpi.name,
                    description: kpi.description || '',
                    target_value: kpi.target_value,
                    current_value: kpi.current_value || 0,
                    unit: kpi.unit,
                    status: kpi.status as 'on-track' | 'at-risk' | 'off-track',
                    frequency: kpi.frequency || 'monthly',
                    owner: employeeName,
                    functional_area: obj.functional_area,
                    progress_percentage: undefined
                  }))
                }))
              )}
            />
          </TabsContent>
            </>
          )}
        </Tabs>
      </div>

      {/* Objective Setting Dialog */}
      <ObjectiveSettingDialog
        open={isObjectiveDialogOpen}
        onClose={() => setIsObjectiveDialogOpen(false)}
        kpas={kpas}
        onCreateObjective={(objective) => {
          // Transform the objective data and call the parent handler
          onCreateObjective({
            title: objective.title,
            description: objective.description,
            role_type: employeeDetails.role,
            owner: employeeName,
            functional_area: employeeDetails.functionalArea || 'Not Specified',
            status: 'draft',
            progress: 0,
            start_date: objective.startDate,
            end_date: objective.endDate,
            strategic_objective_id: objective.strategicObjectiveId || 0,
            kpa_id: parseInt(objective.kpaId) || 0,
            pillar_id: 0
          })
        }}
      />
    </motion.div>
  )
}
